import { nodeVarsOf } from './context.js';
import { runGates } from './gates.js';
import { composerForIdentity } from './composer.js';
import { PERK_PARTICLE_SOURCE } from './perks.js';
import { collectFailures, evaluate } from './predicates.js';
import { applyOutcome, deliverDuePosts } from './outcomes.js';
import { evaluateObjectives } from './world.js';
import { puppetSelectable, swapGrants } from './possession.js';
import { identityNodeId, } from './state.js';
/** Applied when a package references an outcome id that does not exist. */
const SYNTHESISED_SHRUG = { kind: 'shrug' };
/** True while the player is the one speaking, however many voices they are using. */
const isPlayerSend = (source) => source === 'send' || source === 'sync';
/** The evaluation context for one resolution. */
export function evalContextFor(state, request) {
    return {
        aggregate: request.aggregate,
        selection: request.selection,
        particles: request.loaded.particles,
        identity: request.identity,
        resolvingNodeId: request.nodeId,
        nodeVars: nodeVarsOf(state),
        proposal: state.proposal,
        day: state.clock.day,
        phase: state.clock.phase,
        channelId: request.channelId,
        flags: state.flags,
        strikes: state.pip.strikes,
        toneOverride: state.settings.overridesNodeTemperature &&
            request.selection.some((slot) => {
                const particle = request.loaded.particles[slot.particleId];
                return particle?.source === PERK_PARTICLE_SOURCE && particle.slotRole === 'opener';
            }),
    };
}
/**
 * Records the proposal arriving at a node.
 *
 * A send with nothing in flight **starts a chain**: hops reset, and the target
 * becomes the only visited node. A send made while a proposal is already
 * travelling is an interception or a side conversation and leaves the chain
 * alone — its outcome's mutations still reach the proposal, which is exactly
 * how a corrective assembly repairs a pitch mid-flight (design D4).
 */
export function proposalAfterSend(state, targetNodeId, identity = 'self') {
    if (state.inFlight.length > 0)
        return state;
    return {
        ...state,
        proposal: {
            ...state.proposal,
            // Who the pitch came from. Launching it in a puppet's voice makes that
            // node the origin, which is what a `proposal: { origin }` rule reads and
            // what makes laundering an idea through someone else mean anything.
            origin: identityNodeId(identity) ?? 'player',
            hops: 0,
            visited: [targetNodeId],
            holderNodeId: targetNodeId,
            launched: true,
        },
    };
}
/**
 * The in-flight hops a corrective assembly addressed to `nodeId` may amend.
 *
 * A hop names the nodes that can reach it in `interceptableBy`; an empty list
 * means the pitch has left the building and no conversation will call it back.
 */
export function interceptableHops(state, nodeId) {
    return state.inFlight.filter((hop) => hop.interceptableBy.includes(nodeId));
}
/** Records the proposal landing at the next node in the chain. */
export function proposalAfterLanding(state, nodeId) {
    return {
        ...state,
        proposal: {
            ...state.proposal,
            hops: state.proposal.hops + 1,
            visited: state.proposal.visited.includes(nodeId)
                ? state.proposal.visited
                : [...state.proposal.visited, nodeId],
            holderNodeId: nodeId,
            launched: true,
        },
    };
}
/**
 * Applies a voice-mask violation's credibility cost and any lockout.
 *
 * A violation that drops the impression below the puppet's authored floor, or
 * that locks it out for the day, also takes the player *out of that voice*.
 * Leaving them wearing a mask they could no longer choose to put on would let
 * one bad send be followed by any number of free ones in the same voice, and
 * would leave the coordination view saying the puppet is unusable while the
 * composer sends as it.
 */
function applyVoicePenalty(loaded, state, penalty, effects) {
    const puppet = state.puppets[penalty.nodeId];
    if (puppet === undefined || (penalty.credibilityDelta === 0 && penalty.lockedUntilDay === null)) {
        return state;
    }
    const credibility = puppet.credibility + penalty.credibilityDelta;
    effects.push({ type: 'CREDIBILITY_LOST', nodeId: penalty.nodeId, credibility });
    let next = {
        ...state,
        puppets: {
            ...state.puppets,
            [penalty.nodeId]: { ...puppet, credibility, lockedUntilDay: penalty.lockedUntilDay },
        },
        progress: { ...state.progress, credibilityLosses: state.progress.credibilityLosses + 1 },
    };
    if (identityNodeId(next.identity) === penalty.nodeId &&
        !puppetSelectable(loaded, next, penalty.nodeId)) {
        next = swapGrants(loaded, next, next.identity, 'self', effects);
        next = {
            ...next,
            identity: 'self',
            composer: composerForIdentity(loaded, next.composer, 'self'),
        };
        effects.push({ type: 'IDENTITY_CHANGED', identity: 'self' });
    }
    return next;
}
/**
 * Resolves one assembly or one landing proposal against a node.
 *
 * Gates run only for a player send: a proposal travelling between two nodes is
 * not the player speaking, so none of the four gate questions applies to it.
 */
export function resolveAt(state, request) {
    const loaded = request.loaded;
    const node = loaded.nodes[request.nodeId];
    const context = evalContextFor(state, request);
    const particleIds = request.selection.map((slot) => slot.particleId);
    const baseTrace = {
        source: request.source,
        nodeId: request.nodeId,
        channelId: request.channelId,
        identity: request.identity,
        day: state.clock.day,
        phase: state.clock.phase,
        aggregate: request.aggregate,
        particleIds,
    };
    let working = state;
    const effects = [];
    // ---- Interception ------------------------------------------------------
    // A send made while a proposal is travelling only reaches that proposal when
    // the hop names this node on `interceptableBy`. Anything else is a side
    // conversation: it resolves, it has consequences, and the pitch carries on
    // upstairs unamended (design D4).
    const reachable = isPlayerSend(request.source) ? interceptableHops(state, request.nodeId) : [];
    const inFlightNow = isPlayerSend(request.source) && state.inFlight.length > 0;
    const intercepted = inFlightNow && reachable.length > 0;
    const canMutateProposal = !inFlightNow || intercepted;
    if (intercepted) {
        effects.push({
            type: 'PROPOSAL_INTERCEPTED',
            nodeId: request.nodeId,
            hopIds: reachable.map((hop) => hop.id).sort(),
        });
    }
    const outcomeContext = {
        loaded,
        nodeId: request.nodeId,
        channelId: request.channelId,
        identity: request.identity,
        source: request.source,
        reactToMessageId: request.reactToMessageId,
        canMutateProposal,
    };
    // ---- Gates -------------------------------------------------------------
    let gates = [];
    const report = request.gates ??
        (request.source === 'send'
            ? runGates(loaded, state, request.nodeId, request.channelId, context)
            : null);
    if (report !== null) {
        gates = report.results;
        if (report.failure !== null) {
            const failure = report.failure;
            gates = [...report.results, failure.result];
            if (failure.voicePenalty !== null) {
                working = applyVoicePenalty(loaded, working, failure.voicePenalty, effects);
            }
            const trace = {
                ...baseTrace,
                gates,
                firedRuleId: null,
                firedRuleIndex: -1,
                outcomeId: failure.outcomeId,
                outcomeKind: failure.outcome.kind,
                missedRules: [],
                buried: false,
                intercepted,
            };
            effects.push({ type: 'RESOLVED', trace });
            const applied = applyOutcome(working, failure.outcome, outcomeContext);
            const delivered = deliverDuePosts(applied.state);
            const judged = evaluateObjectives(loaded, delivered.state);
            return {
                state: judged.state,
                effects: [...effects, ...applied.effects, ...delivered.effects, ...judged.effects],
                trace,
            };
        }
    }
    // ---- Burial ------------------------------------------------------------
    // The gates asked whether the player was allowed to speak. This asks whether
    // anyone was still looking when they did. A buried message never reaches the
    // decision list, so there are no missed rules to report — the assembly may
    // have been perfect.
    const buriedOutcomeId = request.buriedOutcomeId ?? null;
    if (buriedOutcomeId !== null) {
        const outcome = loaded.outcomes[buriedOutcomeId] ?? SYNTHESISED_SHRUG;
        const trace = {
            ...baseTrace,
            gates,
            firedRuleId: null,
            firedRuleIndex: -1,
            outcomeId: buriedOutcomeId,
            outcomeKind: outcome.kind,
            missedRules: [],
            buried: true,
            intercepted,
        };
        effects.push({ type: 'RESOLVED', trace });
        const applied = applyOutcome(working, outcome, outcomeContext);
        const delivered = deliverDuePosts(applied.state);
        const judged = evaluateObjectives(loaded, delivered.state);
        return {
            state: judged.state,
            effects: [...effects, ...applied.effects, ...delivered.effects, ...judged.effects],
            trace,
        };
    }
    // ---- Decision list -----------------------------------------------------
    const rules = node?.resolve.rules ?? [];
    let firedIndex = -1;
    for (let index = 0; index < rules.length; index += 1) {
        const rule = rules[index];
        if (rule === undefined)
            continue;
        if (evaluate(rule.when, context)) {
            firedIndex = index;
            break;
        }
    }
    const fired = firedIndex >= 0 ? rules[firedIndex] : undefined;
    const outcomeId = fired?.then ?? node?.resolve.default ?? '';
    const outcome = loaded.outcomes[outcomeId] ?? SYNTHESISED_SHRUG;
    // Every rule above the one that fired, with the leaves that kept it out.
    // When nothing fired, that is every rule the node declares.
    const considered = firedIndex >= 0 ? rules.slice(0, firedIndex) : rules;
    const missedRules = considered.map((rule, index) => ({
        ruleId: rule.id,
        index,
        failingLeaves: collectFailures(rule.when, context),
        hintRefs: rule.hintRefs ?? null,
    }));
    const trace = {
        ...baseTrace,
        gates,
        firedRuleId: fired?.id ?? null,
        firedRuleIndex: firedIndex,
        outcomeId,
        outcomeKind: outcome.kind,
        missedRules,
        buried: false,
        intercepted,
    };
    effects.push({ type: 'RESOLVED', trace });
    const applied = applyOutcome(working, outcome, outcomeContext);
    const delivered = deliverDuePosts(applied.state);
    // Win and loss are re-asked after every resolution, so a proposal that
    // satisfies the objective on landing ends the mission there rather than at the
    // end of the phase.
    const judged = evaluateObjectives(loaded, delivered.state);
    return {
        state: judged.state,
        effects: [...effects, ...applied.effects, ...delivered.effects, ...judged.effects],
        trace,
    };
}
//# sourceMappingURL=resolve.js.map